import { mergeWith } from "lodash"
import { isValidElement, PropsWithChildren, Suspense, useEffect, useMemo, useState } from "react"
import { useMediaQuery } from "react-responsive"
import { Outlet } from "react-router-dom-7"

import { ocpoContactInfo } from "~/templates/main/contact-info"
import { userSessionStore } from "~/domain/user-session/model"
import { callIfFunc, inv } from "~/shared/lib/utils"
import { ErrorBoundary, Loading } from "~/shared/ui"
import { Layout as _Layout, useContentContainerContext } from "~/shared/ui/layouts/main"
import type { LayoutProps } from "~/shared/ui/layouts/main/types"

import { FooterContent } from "./FooterContent"
import { MainTemplateContext } from "./MainTemplateContext"
import { MainUserbar } from "./MainUserbar"
import { SidebarSwipe } from "./SidebarSwipe"
import { ErrorMessageFallback } from "~/shared/ui/components/errors/ErrorMessageFallback"
import { breakPoints } from "~/shared/ui/constants"
import { useLocation, useNavigationType } from "react-router-dom-7"

function ContentScrollRestoration() {

	const { contentContainerRef } = useContentContainerContext()
	const location = useLocation()
	const navigationType = useNavigationType()
	const scrollMem = useMemo(() => new Map<string, number>(), [])

	useEffect(() => {

		if (!contentContainerRef.current) return

		const el = contentContainerRef.current
		const handleScroll = () => {
			scrollMem.set(location.pathname, el.scrollTop)
		}
		el.addEventListener("scroll", handleScroll)

		return () => el.removeEventListener("scroll", handleScroll)


	}, [location.pathname, contentContainerRef.current])

	useEffect(() => {

		if (!contentContainerRef.current) return

		contentContainerRef.current.scrollTop = navigationType === "POP"
			? scrollMem.get(location.pathname) ?? 0
			: 0

	}, [location.pathname, navigationType])

	return null
}

export type MainTemplateProps = LayoutProps & { documentTitle?: string | ((contentTitle?: string) => string) }

/**
 * Главный шаблон, основанный на [главном макете](/docs/shared-ui-layouts-main--docs). Может принимать все свойства главного макета, а также доп. свойство `documentTitle`.
 * Многие свойства из макета уже имеют значения по умолчанию. В примере приведены свойства с которыми вы будете иметь дело в большинстве случаев.
 * Остальные свойства см. в документации главного макета.
 * Чтобы устанавливать для каждой страницы свои свойства шаблона, используйте хук `useMainTemplateProps` или хок `withMainTemplateProps` из этого же модуля.
 */
export function MainTemplate({
								 children,
								 ...props
							 }: PropsWithChildren<MainTemplateProps>) {

	const [layoutProps, setLayoutProps] = useState({} as MainTemplateProps)
	const [hideSidebar, setHideSidebar] = useState(props.sidebar?.hide ?? true)

	const isMinWidthSm = useMediaQuery({ minWidth: breakPoints.sm })

	useEffect(() => {
		const title =
			layoutProps.documentTitle ||
			props.documentTitle ||
			layoutProps.content?.title ||
			props.content?.title
		document.title = title
			? "ЭИОС | " + callIfFunc(title, layoutProps.content?.title || props.content?.title)
			: "ЭИОС"
	}, [
		props.documentTitle,
		layoutProps.documentTitle,
		props.content?.title,
		layoutProps.content?.title
	])

	useEffect(() => {
		// TODO: отделить инф-ю о кол-ве сообщений и убрать отсюда?
		if (userSessionStore.userInfo) {
			void userSessionStore.refreshStudentUserInfo()
		}
	}, [])

	let sidebarLinks = layoutProps.sidebar?.links ?? props.sidebar?.links

	if (!isMinWidthSm) {
		sidebarLinks = sidebarLinks?.map(link => ({
			...link,
			onClick() {
				setHideSidebar(true)
				link.onClick?.()
			}
		}))
	}

	return (
		<>
			<SidebarSwipe setHideSidebar={setHideSidebar} />
			<_Layout
				{...mergeWith(
					{
						footer: {
							content: (
								<FooterContent contacts={ocpoContactInfo} />
							)
						},
						userbar: { render: () => <MainUserbar /> }
					} satisfies LayoutProps,
					props,
					layoutProps,
					{
						sidebar: {
							enableTransition: true,
							links: sidebarLinks,
							hide:
								layoutProps.sidebar &&
								layoutProps.sidebar.links &&
								layoutProps.sidebar.links.length > 0
									? isMinWidthSm
										? layoutProps.sidebar?.hide
										: layoutProps.sidebar?.hide || hideSidebar
									: true,
							onDarkAreaClick: () => setHideSidebar(true)
						},
						logo: {
							showMenuButton: sidebarLinks && sidebarLinks.length > 0,
							onMenuButtonClick: () => setHideSidebar(inv),
							onClick: () => setHideSidebar(true)
						}
					} satisfies LayoutProps,
					(/*objValue*/_, srcValue) => {
						return isValidElement(srcValue) ? srcValue : undefined
					}
				)}
			>
				<Suspense fallback={<Loading loading delay={250} />}>
					<ErrorBoundary fallback={<ErrorMessageFallback />}>
						<MainTemplateContext.Provider
							value={{
								layoutProps,
								setLayoutProps
							}}
						>
							{children ?? <Outlet />}
						</MainTemplateContext.Provider>
					</ErrorBoundary>
				</Suspense>
				<ContentScrollRestoration />
			</_Layout>
		</>
	)
}
